import React, { useCallback, useMemo, useState } from 'react';
import '../styles/styles.css';
import '../styles/formularios.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { Link } from 'react-router-dom';
import logoInstituto from '../components/images/logo.png';
import logoSEP from '../components/images/SEP_Logo_2019.svg.png';
import Swal from 'sweetalert2';
import { resolveApiBaseList } from '../utils/apiBase';

const TraspasoHistoricoRegistro = () => {
  const apiBases = useMemo(() => resolveApiBaseList(), []);
  const [procesando, setProcesando] = useState(false);
  const [paso1Realizado, setPaso1Realizado] = useState(false);

  const fetchWithFallback = useCallback(async (path, options = {}, parse = 'json') => {
    let lastError = null;

    for (const base of apiBases) {
      const normalizedBase = base.replace(/\/$/, '');
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      const url = `${normalizedBase}${normalizedPath}`;

      try {
        const response = await fetch(url, options);

            if (!response.ok) {
              let message = '';
              try {
                const text = await response.text();
                message = text || `Error ${response.status}`;
                try {
                  const parsed = text ? JSON.parse(text) : null;
                  if (parsed && typeof parsed === 'object') {
                    message = parsed.detalle || parsed.message || parsed.error || message;
                  }
                } catch {
                  // mantener mensaje original
                }
              } catch {
                message = `Error ${response.status}`;
              }

          const error = new Error(message);
          error.status = response.status;
          lastError = error;
          console.warn(`Warning: petición fallida a ${url}:`, error);
          continue;
        }

        if (parse === 'blob') {
          const blob = await response.blob();
          return { data: blob, baseUrl: normalizedBase, response };
        }

        if (parse === 'text') {
          const text = await response.text();
          return { data: text, baseUrl: normalizedBase, response };
        }

        const text = await response.text();
        const trimmed = text.trim();
        if (!trimmed) {
          return { data: {}, baseUrl: normalizedBase, response };
        }

        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/json') || trimmed.startsWith('{') || trimmed.startsWith('[')) {
          return { data: JSON.parse(trimmed), baseUrl: normalizedBase, response };
        }

        try {
          return { data: JSON.parse(trimmed), baseUrl: normalizedBase, response };
        } catch {
          lastError = new Error(`Respuesta inválida desde ${url}`);
          console.warn(`Warning: contenido no JSON desde ${url} (content-type: ${contentType || 'desconocido'})`);
        }
      } catch (error) {
        lastError = error;
        console.warn(`Warning: error de red al solicitar ${url}:`, error);
      }
    }

    throw lastError || new Error('No se pudo contactar al servidor.');
  }, [apiBases]);

  // =========================
  //  NUEVO: helpers traspaso
  // =========================
  const apiTraspaso = useCallback(async ({ dryRun = true, purge = false, planeaciones = false, evidencias = false } = {}) => {
    const qs = new URLSearchParams();
    if (dryRun) qs.set('dryRun', 'true');
    if (purge) qs.set('purge', 'true');
    if (planeaciones) qs.set('planeaciones', 'true');
    if (evidencias) qs.set('evidencias', 'true');

    const query = qs.toString();
    const path = `/traspaso/historico${query ? `?${query}` : ''}`;
    const { data } = await fetchWithFallback(path, { method: 'POST' });
    return data;
  }, [fetchWithFallback]);

  const renderResumenHTML = (resumen) => {
    if (!resumen) return '<em>Sin datos</em>';
    const safe = (n) => (typeof n === 'number' ? n : 0);
    return `
      <div class="text-start">
        <ul class="mb-0">
          <li><strong>Usuarios en baja:</strong> ${safe(resumen.usuarios_baja)}</li>
          <li><strong>Grupos (ciclo vencido):</strong> ${safe(resumen.grupos_ciclo_vencido)}</li>
          <li><strong>Alumnos asociados:</strong> ${safe(resumen.alumnos_asociados)}</li>
          <li><strong>Lista de Grupos asociados:</strong> ${safe(resumen.listagrupos_asociados)}</li>
          <li><strong>Horarios:</strong> ${safe(resumen.horarios)}</li>
          <li><strong>Materias en baja:</strong> ${safe(resumen.materias_baja)}</li>
          <li><strong>Listas Trim 3:</strong> ${safe(resumen.listas_trim3)}</li>
          <li><strong>Detalle Listas Trim 3:</strong> ${safe(resumen.det_listas_trim3)}</li>
        </ul>
      </div>
    `;
  };

  // =========================
  //  Reemplazo interno: ahora SÍ hace el traspaso
  // =========================
  const confirmarTraspaso = async () => {
    try {
      // 1) Vista previa (dry run)
      setProcesando(true);
      const preview = await apiTraspaso({ dryRun: true });

      const decide = await Swal.fire({
        title: 'Vista previa del traspaso',
        html: `
          <p class="mb-2">Esto es lo que se <strong>moverá</strong> a histórico:</p>
          ${renderResumenHTML(preview.resumen)}
          <hr/>
          <p class="mb-0">¿Deseas continuar y <strong>mover + borrar en primariaisc</strong>?</p>
        `,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sí, ejecutar traspaso',
        cancelButtonText: 'Cancelar',
        reverseButtons: true,
        allowOutsideClick: false,
        allowEscapeKey: false
      });

      if (!decide.isConfirmed) return;

      // 2) Ejecutar traspaso real con purge (borra en primariaisc SIN tocar AUTO_INCREMENT)
      const result = await apiTraspaso({ dryRun: false, purge: true });

      await Swal.fire({
        title: 'Traspaso completado',
        html: `
          <p class="mb-2">Se movió la información a <code>primischistori</code> y se eliminó de <code>primariaisc</code>.</p>
          ${renderResumenHTML(result.resumen)}
        `,
        icon: 'success',
        confirmButtonText: 'Aceptar',
        allowOutsideClick: false
      });
    } catch (err) {
      console.error('Error en traspaso histórico:', err);
      await Swal.fire({
        title: 'Error en el traspaso',
        text: err.message,
        icon: 'error',
        confirmButtonText: 'Aceptar',
        allowOutsideClick: false
      });
    } finally {
      setProcesando(false);
    }
  };

  const limpiarCarpetasDocs = useCallback(async () => {
    await fetchWithFallback('/utilerias/docs-limpiar', { method: 'POST' }, 'text');
  }, [fetchWithFallback]);

  const descargarZip = useCallback(async () => {
    const { data: contenido, response } = await fetchWithFallback('/utilerias/docs-zip', {}, 'blob');
    const disposicion = response?.headers?.get('Content-Disposition') || '';
    const coincidencia = /filename="?([^";]+)"?/i.exec(disposicion);
    const nombreArchivo = coincidencia ? coincidencia[1] : 'planeaciones-evidencias.zip';

    const url = window.URL.createObjectURL(contenido);
    const enlace = document.createElement('a');
    enlace.href = url;
    enlace.download = nombreArchivo;
    document.body.appendChild(enlace);
    enlace.click();
    document.body.removeChild(enlace);
    window.URL.revokeObjectURL(url);
  }, [fetchWithFallback]);

  const handleResguardo = async () => {
    if (procesando) return;
    setProcesando(true);

    try {
      const { data } = await fetchWithFallback('/utilerias/docs-metadata');
      const size = data.readableSize || '0 B';
      const total = data.fileCount ?? 0;
      const decisionInicial = await Swal.fire({
        title: 'Resguardar planeaciones y evidencias',
        html: `<p class="mb-2">La carpeta contiene <strong>${total}</strong> archivo(s) con un peso aproximado de <strong>${size}</strong>.</p>
               <p class="mb-0">¿Deseas descargar un archivo ZIP con todo el contenido antes de limpiar las carpetas?</p>`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sí, descargar ZIP',
        cancelButtonText: 'No, continuar sin respaldo',
        reverseButtons: true,
        allowOutsideClick: false,
        allowEscapeKey: false,
      });

      if (decisionInicial.isConfirmed) {
        await descargarZip();
        await limpiarCarpetasDocs();
        setPaso1Realizado(true);
        await Swal.fire({
          title: 'Resguardo completado',
          text: 'Se descargó el respaldo y se limpiaron las carpetas de planeaciones y evidencias.',
          icon: 'success',
          confirmButtonText: 'Continuar',
          allowOutsideClick: false,
        });
        return;
      }

      if (decisionInicial.dismiss === Swal.DismissReason.cancel) {
        const confirmacionFinal = await Swal.fire({
          title: '¿Seguro que deseas continuar sin resguardar?',
          html: '<p class="mb-2">Si decides no resguardar, <strong>no podrás recuperar los archivos almacenados</strong>.</p><p class="mb-0">Esta acción eliminará todo el contenido de las carpetas de planeaciones y evidencias.</p>',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Sí, limpiar carpetas',
          cancelButtonText: 'Regresar',
          reverseButtons: true,
          allowOutsideClick: false,
          allowEscapeKey: false,
        });

        if (confirmacionFinal.isConfirmed) {
          await limpiarCarpetasDocs();
          setPaso1Realizado(true);
          await Swal.fire({
            title: 'Carpetas limpiadas',
            text: 'Se eliminó el contenido de planeaciones y evidencias del ciclo actual.',
            icon: 'success',
            confirmButtonText: 'Aceptar',
            allowOutsideClick: false,
          });
        } else {
          await Swal.fire({
            title: 'Operación cancelada',
            text: 'No se realizaron cambios. Puedes volver a intentarlo cuando estés listo.',
            icon: 'info',
            confirmButtonText: 'Entendido',
            allowOutsideClick: false,
          });
        }
      }
    } catch (error) {
      console.error('Error durante el resguardo previo:', error);
      await Swal.fire({
        title: 'Error durante el resguardo',
        text: error.message,
        icon: 'error',
        confirmButtonText: 'Aceptar',
        allowOutsideClick: false,
      });
    } finally {
      setProcesando(false);
    }
  };

  return (
    <div className="container mt-4">
      {/* Encabezado */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <img src={logoInstituto} alt="Logo Instituto" style={{ height: '60px' }} />
        <h4 className="text-center flex-grow-1 mb-0">
          Instituto Silviano Carrillo<br />
          <small className="text-muted">Traspaso Histórico</small>
        </h4>
        <img src={logoSEP} alt="Logo SEP" style={{ height: '60px' }} />
      </div>

      {/* Introducción */}
      <div className="bg-light p-4 rounded shadow-sm mb-4">
        <h5><i className="fa fa-clock"></i> ¿Qué es el Traspaso Histórico?</h5>
        <p className="mb-0">
          El traspaso histórico es el proceso mediante el cual se resguardan oficialmente todo el formato académico generado durante el ciclo escolar actual, para su archivo institucional y futuras consultas.
        </p>
      </div>

      {/* Paso 1: Resguardo de archivos */}
      <div className="bg-white p-4 rounded shadow-sm">
        <h6 className="mb-3">
          <i className="fa fa-folder-open text-primary"></i> Paso 1: Resguardar planeaciones y evidencias
        </h6>

        <p className="text-muted">
          Primero genera un respaldo opcional de todas las planeaciones y evidencias almacenadas. Podrás descargar un ZIP con el contenido o continuar sin descargarlo.
        </p>

        <div className="text-center">
          <button
            className="btn btn-primary px-4 py-2"
            onClick={handleResguardo}
            disabled={procesando}
          >
            <i className="fa fa-download me-2"></i>
            {procesando ? 'Procesando...' : 'Resguardar Planeaciones y Evidencias'}
          </button>
        </div>

        {paso1Realizado && (
          <p className="mt-3 text-success text-center">
            <i className="fa fa-check-circle me-2"></i>Resguardo previo completado. Continúa con el traspaso histórico.
          </p>
        )}
      </div>

      {/* Paso 2: Confirmación */}
      {paso1Realizado && (
        <div className="bg-white p-4 rounded shadow-sm mt-4 text-center" id="contenedorTraspaso">
          <h6 className="mb-3 text-success">
            <i className="fa fa-check-circle"></i> Todo listo para el Traspaso Histórico
          </h6>
          <p className="text-muted mb-4">
            ¿Estás seguro de que deseas realizar el traspaso histórico de planeaciones y evidencias? Esta acción no se puede deshacer.
          </p>

          <button className="btn btn-success px-5 py-2" onClick={confirmarTraspaso} disabled={procesando}>
            <i className="fa fa-database"></i> {procesando ? 'Procesando...' : 'Realizar Traspaso Histórico'}
          </button>
          <Link to="/directora/inicio" className="btn btn-danger px-5 py-2 ms-3">
            <i className="fa fa-times"></i> Salir
          </Link>

        </div>
      )}
    </div>
  );
};

export default TraspasoHistoricoRegistro;
